import { EmptyState, ErrorState, LoadMore, Skeleton, StatusBadge } from '@shared/design-system/ui'
import type { StatusTone } from '@shared/design-system/ui'
import { ShoppingCart } from 'lucide-react'
import type { RecuperaCartItem } from '../api/recupera-ai.contract'
import {
  avatar,
  cellMuted,
  cellStrong,
  customerCell,
  table,
  tableWrap,
  td,
  th,
  valueCell,
} from './recupera-ai.css'

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('pt-BR')
}

function formatRelative(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const minutes = Math.round(diffMs / 60000)
  if (minutes < 60) return `${Math.max(minutes, 0)} min atrás`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h atrás`
  const days = Math.round(hours / 24)
  if (days < 365) return `${days} d atrás`
  const years = Math.round(days / 365)
  return `${years} a atrás`
}

function formatCurrency(raw: string, currency: string): string {
  const parsed = Number.parseFloat(raw)
  if (Number.isNaN(parsed)) return `${currency} ${raw}`
  return parsed.toLocaleString('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function value(item: RecuperaCartItem): string {
  const raw = item.paid_value ?? item.product_price
  return raw === null || raw === undefined ? '-' : formatCurrency(raw, item.currency ?? 'BRL')
}

function initialOf(item: RecuperaCartItem): string {
  const source = item.customer_name ?? item.customer_email ?? item.phone ?? '?'
  return source.trim().charAt(0).toUpperCase() || '?'
}

const PRODUCT_TYPE_LABEL: Record<'main' | 'upsell', string> = {
  main: 'Produto principal',
  upsell: 'Upsell',
}

function statusBadge(item: RecuperaCartItem): { tone: StatusTone; label: string } {
  const recoveryType = item.recovery_type ?? (item.recovered_at !== null ? 'recuperaai' : null)
  if (recoveryType === 'recuperaai') return { tone: 'success', label: 'Recuperado' }
  if (recoveryType === 'external')
    return { tone: 'informative', label: 'Convertido (outra origem)' }
  return { tone: 'awaiting', label: 'Em andamento' }
}

interface RecuperaCartsTableProps {
  items: readonly RecuperaCartItem[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}

export function RecuperaCartsTable({
  items,
  isLoading,
  isError,
  onRetry,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: RecuperaCartsTableProps) {
  if (isLoading) return <Skeleton width="100%" height={240} />
  if (isError) return <ErrorState onRetry={onRetry} />
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<ShoppingCart size={23} />}
        title="Nenhum carrinho por aqui"
        description="Ainda não há carrinhos abandonados ou recuperados para o período selecionado."
      />
    )
  }

  return (
    <div className={tableWrap} data-testid="recupera-ai-table">
      <table className={table}>
        <thead>
          <tr>
            <th className={th}>Cliente</th>
            <th className={th}>Produto</th>
            <th className={th}>Valor</th>
            <th className={th}>Tempo</th>
            <th className={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const badge = statusBadge(item)
            const productType = item.product_type ?? 'main'
            return (
              <tr key={item.order_id} data-testid={`recupera-ai-row-${item.order_id}`}>
                <td className={td}>
                  <div className={customerCell}>
                    <span className={avatar} aria-hidden>
                      {initialOf(item)}
                    </span>
                    <div>
                      <div className={cellStrong}>{item.customer_name ?? '-'}</div>
                      <div className={cellMuted}>{item.customer_email ?? item.phone ?? '-'}</div>
                    </div>
                  </div>
                </td>
                <td className={td}>
                  <div className={cellStrong}>{item.product_name ?? '-'}</div>
                  <div className={cellMuted}>{PRODUCT_TYPE_LABEL[productType]}</div>
                </td>
                <td className={td}>
                  <span className={valueCell}>{value(item)}</span>
                </td>
                <td className={td}>
                  <div>{formatDateTime(item.abandoned_at)}</div>
                  <div className={cellMuted}>{formatRelative(item.abandoned_at)}</div>
                </td>
                <td className={td}>
                  <StatusBadge tone={badge.tone} label={badge.label} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <LoadMore
        hasMore={hasMore}
        loading={isFetchingMore}
        onClick={onLoadMore}
        shown={items.length}
      />
    </div>
  )
}
